import { Fragment, useEffect, useMemo, useRef } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { Banner, Checkbox, Input, Typography } from '@weareopenr/component-library';
import classnames from 'classnames/bind';

import { pluralize } from 'src/helpers/pluralize/pluralize';
import { usePriceSchemes } from 'src/hooks/usePriceSchemes/usePriceSchemes';
import { useSelectedPriceScheme } from 'src/hooks/useSelectedPriceScheme/useSelectedPriceScheme';
import { IOptionGroupValues } from '../CreateOptionGroupModal/types';

import styles from './OptionGroupPricing.module.scss';

const cx = classnames.bind(styles);

interface IOptionGroupPricingProps {
  formReturn: UseFormReturn<IOptionGroupValues>;
}

export const OptionGroupPricing = ({ formReturn }: IOptionGroupPricingProps) => {
  const {
    register,
    setValue,
    control,
    formState: { errors },
    trigger,
  } = formReturn;
  const initialPricesRef = useRef<Record<number, number | null | undefined>>({});
  const priceInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const { selectedPriceSchemeId } = useSelectedPriceScheme();
  const { data: allPriceSchemes = [] } = usePriceSchemes();
  const optionGroupName = useWatch({ control, name: 'name' });
  const modifiers = useWatch({ control, name: 'modifiers' });
  const priceSchemeIds = useWatch({ control, name: 'price_scheme_ids' });

  const resolvedPriceSchemeIds = useMemo(() => {
    if (priceSchemeIds?.length) return priceSchemeIds;
    if (selectedPriceSchemeId) return [selectedPriceSchemeId];
    return [];
  }, [priceSchemeIds, selectedPriceSchemeId]);

  const priceSchemes = useMemo(
    () => allPriceSchemes.filter((scheme) => resolvedPriceSchemeIds.includes(scheme.id)),
    [allPriceSchemes, resolvedPriceSchemeIds],
  );

  const priceSchemesNames = useMemo(() => priceSchemes.map((scheme) => scheme.name).join(', '), [priceSchemes]);

  useEffect(() => {
    Object.values(priceInputRefs.current).forEach((input) => {
      if (!input || input.value === '') return;
      const parsed = Number(input.value);
      if (!Number.isNaN(parsed)) input.value = parsed.toFixed(2);
    });
  }, []);

  return (
    <div className={cx('flex-column', 'container')}>
      <div>
        <Typography size="xxlarge" className={styles['heading-text']}>
          Set prices for <span className={cx('bold-text')}>{optionGroupName}</span>
        </Typography>
        <Typography>Define the prices of products in your group for a specific Price Scheme</Typography>
      </div>
      <Typography>
        {pluralize('Price Scheme', priceSchemes.length)} - <span className={cx('bold-text')}>{priceSchemesNames}</span>
      </Typography>
      <div className={cx('grid')}>
        <Typography weight="bold">Product Name</Typography>
        <Typography weight="bold" className={cx('checkbox-heading')}>
          Inherit Product Price
        </Typography>
        <Typography weight="bold">Override Price*</Typography>
        {modifiers?.map((field, index) => {
          if (!(index in initialPricesRef.current)) {
            initialPricesRef.current[index] = field.valueWithTax ?? null;
          }
          const priceRegister = register(`modifiers.${index}.valueWithTax`, {
            onChange: () => {
              trigger('modifiers');
            },
            onBlur: (e) => {
              if (e.target.value === '') {
                const openedWithValue = initialPricesRef.current[index] ?? null;
                setValue(`modifiers.${index}.valueWithTax`, openedWithValue, { shouldValidate: true });
              }
            },
            valueAsNumber: true,
            validate: (value, formValues) => {
              if (formValues.modifiers[index].useDefaultPrice) return true;
              if (value === null || value === undefined || Number.isNaN(value)) return 'Please enter a price';
              if (value < 0) return 'Price cannot be negative';
              return true;
            },
          });
          return (
            <Fragment key={field.optionItemId}>
              <Typography className={cx('truncate-text')}>{modifiers[index].name}</Typography>
              <div className={cx('checkbox')}>
                <Checkbox
                  checked={modifiers[index].useDefaultPrice}
                  onChange={() => {
                    setValue(`modifiers.${index}.useDefaultPrice`, !modifiers[index].useDefaultPrice);
                    setValue(`modifiers.${index}.valueWithTax`, null, { shouldValidate: true });
                  }}
                />
              </div>
              <Input
                type="number"
                step={0.01}
                label="Price (£)"
                disabled={field.useDefaultPrice}
                register={{
                  ...priceRegister,
                  ref: (node: HTMLInputElement | null) => {
                    priceRegister.ref(node);
                    priceInputRefs.current[index] = node;
                  },
                }}
                error={errors?.modifiers?.[index]?.valueWithTax?.message}
              />
            </Fragment>
          );
        })}
      </div>
      <Banner
        text={
          <>
            <strong>Quick note</strong> - These prices will only be added to your {priceSchemesNames}{' '}
            {pluralize('scheme', priceSchemes.length)}. In order to use this group with alternative schemes, you will
            need to enter prices within those schemes
          </>
        }
      />
    </div>
  );
};

export default OptionGroupPricing;
